<template>
  <v-container>
    <v-row>
      <v-col>
        <DragAndDropFile
            ref="dragAndDrop"
            custom-height="80vh"
            not-required
            :view-only="!isNewItem"
            @fileChange="setFile($event)"
        />
      </v-col>
      <v-col>
        <v-form ref="form">
          <v-row justify="space-between">
            <v-col cols="12" :md="signMode ? 4 : 6">
              <v-container>
                <v-row>
                  <v-col>
                    <CustomerTypeAhead
                        ref="customerTypeahead"
                        :value="invoiceDocumentation.supplierId"
                        @input="($event) => invoiceDocumentation.supplierId = $event"
                        @inputName="($event) => invoiceDocumentation.supplier = $event"
                        :label="$t('book/issue:common.supplier')"
                        required
                        :suppliersOnly="true"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="invoiceDocumentation.price"
                      :readonly="readOnly"
                      clearable
                      outlined
                      :label="$t('common:price')"/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" :md="signMode ? 4 : 6">
              <v-container>
                <v-row>
                  <v-col>
                    <DateField
                      ref="validFrom"
                      @update="(v) => invoiceDocumentation.startDate = v"
                      :isClearable="true"
                      :key="startDateKey"
                      :readonly="readOnly"
                      :label="$t('book/issue:invoice:valid-from')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <DateField
                      ref="validUntil"
                      @update="(v) => invoiceDocumentation.endDate = v"
                      :isClearable="true"
                      :key="endDateKey"
                      :readonly="readOnly"
                      :label="$t('book/issue:invoice:valid-until')"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" md="4" v-if="signMode">
              <v-checkbox
                :label="$t('book/issue:common.factually-correct')"
                :readonly="isProcessed || isSigned"
                v-model="invoiceDocumentation.factuallyCorrect" />
              <v-checkbox
                :label="$t('book/issue:common.technically-correct')"
                :readonly="isProcessed || isSigned"
                v-model="invoiceDocumentation.technicallyCorrect" />
              <v-checkbox :label="$t('common:signed')"
                :readonly="isProcessed || isSigned"
                v-model="invoiceDocumentation.signed" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-container>
                <v-col>
                  <v-row>
                    <v-text-field
                        v-model="invoiceDocumentation.invoiceNumber"
                        :readonly="readOnly"
                        clearable
                        outlined
                        :label="$t('book/issue:common.invoice-number')"/>
                  </v-row>
                  <v-row>
                    <v-textarea
                      :label="$t('common:description')"
                      :readonly="readOnly"
                      clearable
                      outlined
                      v-model="invoiceDocumentation.description" />
                  </v-row>
                </v-col>
                </v-container>
            </v-col>
          </v-row>
          <v-row v-if="signMode && !isFinish">
            <v-col>
              <BaseButton
                color="primary"
                outlined
                :btnText="$t('book/issue:add-issue-form.sign')"
                @clickedButton="signInvoice"
                :disabled="isSigned"
              />
            </v-col>
            <v-col>
              <BaseButton
                color="primary"
                outlined
                :btnText="$t('book/issue:add-issue-form.payment-order')"
                @clickedButton="processInvoice"
                :disabled="isProcessed"
              />
            </v-col>
          </v-row>
          <v-row v-else-if="!readOnly && !hideButton && !isFinish">
            <v-col>
              <TwoButtonDialog
                ref="saveButton"
                :btn-text="buttonLabel"
                color="primary"
                outlined
                :dialog-title="$t(isNewItem ? 'dialog:add-btn-title' : 'dialog:save-btn-dialog', {name : $t('book/issue:invoiceDocumentation')})"
                :dialog-text="$t(isNewItem ? 'dialog:add-btn-text' : 'dialog:save-btn-dialog', {name : $t('book/issue:invoiceDocumentation')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t(isNewItem ? 'buttons:add' : 'buttons:save')"
                @clickedButton1="$refs.saveButton.showDialog = false"
                @clickedButton2="save"
                hide-overlay
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>

import InvoiceDocumentationService from '@/modules/book/services/task-documentation/invoice-documentation-service';
import OfferDocumentationService from '@/modules/book/services/task-documentation/offer-documentation-service';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog.vue';
import DocumentationService from '@/modules/core/services/documentation-service';
import InvoiceDocumentation from '@/modules/book/data/invoice-documentation';
import TaskBoardService from '@/modules/book/services/task-board-service';
import DragAndDropFile from '@/modules/core/components/DragAndDropFile';
import BaseButton from '@/modules/core/components/Base/BaseButton';
import {DocumentType} from '@/modules/book/data/document-type';
import DateField from '@/modules/core/components/DateField';
import {ruleRequired} from '@/modules/core/services/rules';
import Utils from '@/modules/core/services/utils';
import CustomerTypeAhead from "@/modules/core/components/CustomerTypeAhead.vue";


export default {
  name: 'InvoiceDocumentationForm',
  components: {CustomerTypeAhead, TwoButtonDialog, BaseButton, DragAndDropFile, DateField},
  props: {
    hideButton: Boolean,
    readOnly: Boolean,
    taskInfo: {},
    disableSign: Boolean,
    disableProcess: Boolean,
    signMode: Boolean
  },
  data() {
    return {
      file: {},
      invoiceDocumentation: new InvoiceDocumentation(),
      startDateKey: 0,
      endDateKey: 1,
      isNewItem: false,
      recentlySigned: false,
      recentlyProcessed: false,
    }
  },
  computed: {
    buttonLabel() {
      if (this.isNewItem)
        return this.$t('buttons:add-specific', { specific: this.$t('book/issue:invoiceDocumentation') });

      return this.$t('save-changes');
    },
    isSigned() {
      return this.disableSign || this.recentlySigned;
    },
    isProcessed() {
      return this.disableProcess || this.recentlyProcessed;
    },
    isFinish() {
      return this.taskInfo && this.taskInfo.finish;
    }
  },
  methods: {
    ruleRequired,
    setInputData(inputData) {
      this.invoiceDocumentation.supplier = null;
      this.invoiceDocumentation.task = inputData.taskId;
      this.invoiceDocumentation.customer = inputData.customerId;
      if (inputData.signedOffer) {
        this.loadLiferantBySignedOffer(inputData.signedOffer);
      }

      if (inputData.file) {
        this.$refs.dragAndDrop.uploadAsUrl(inputData.file);
        this.file = inputData.file;
      }
      this.isNewItem = true;

    },
    setEditData(invoice) {
      if (!invoice) return;
      this.invoiceDocumentation = invoice;
      this.recentlyProcessed = false;
      this.recentlySigned = false;

      if (this.invoiceDocumentation.startDate) {
        this.invoiceDocumentation.startDate =
            this.$refs.validFrom.parseDTO(this.invoiceDocumentation.startDate);
      }

      if (this.invoiceDocumentation.endDate) {
        this.invoiceDocumentation.endDate =
            this.$refs.validUntil.parseDTO(this.invoiceDocumentation.endDate);
      }

      if (this.invoiceDocumentation.supplierId !== null)
        this.$refs.customerTypeahead.initialise(this.invoiceDocumentation.supplierId);

      this.loadStaticFileUrl();
    },
    save() {
      this.$refs.dragAndDrop.isMandatoryFilledOut();
      if (!this.$refs.form.validate()
          || !this.$refs.dragAndDrop.isMandatoryFilledOut()) {
        document.getElementsByClassName("v-dialog--active")[0].scrollTop = 0;
        this.$refs.saveButton.showDialog = false;
        return;
      }

      let label = this.invoiceDocumentation.id ? 'snackbar:update-success' : 'snackbar:add-success';
      let failLabel = this.invoiceDocumentation.id ? 'snackbar:update-failure' : 'snackbar:add-failure';
      InvoiceDocumentationService.saveOrUpdate(
          this.invoiceDocumentation,
          this.file,
          () => {
            Utils.showSuccessSnackbar(this.$t(label, {name: this.$t('book/issue:invoiceDocumentation')}))
            this.$emit('closeDialog');
          },
          () => {
            Utils.showFailureSnackbar(this.$t(failLabel, {name: this.$t('book/issue:invoiceDocumentation')}))
            this.$emit('closeDialog');
          }
      );
    },
    cleanForm() {
      this.invoiceDocumentation = new InvoiceDocumentation();
      this.$refs.form.reset();
      this.startDateKey++;
      this.endDateKey--;
      this.isNewItem = false;
      this.recentlyProcessed = false;
      this.recentlySigned = false;
      this.$refs.dragAndDrop.clear();
    },
    loadStaticFileUrl(cleanCache = false) {
      DocumentationService.getDocByTypeAndId(
          this.invoiceDocumentation.id,
          this.invoiceDocumentation.fileType,
          DocumentType.INVOICE_TASK,
          this.invoiceDocumentation.task,
          (fileUrl) => {
            this.invoiceDocumentation.src = fileUrl;
            this.$refs.dragAndDrop.setPreviewSrc(this.invoiceDocumentation);
          },
          () => {
            this.$refs.dragAndDrop.clear();
          },
          cleanCache
      )
    },
    signInvoice() {
      TaskBoardService.signInvoice(
          this.invoiceDocumentation.task,
          this.invoiceDocumentation.id,
          () => {
            Utils.showSuccessSnackbar(this.$t('book/issue:invoiceDocumentation'));
            this.recentlySigned = true;
            this.loadStaticFileUrl(true);
            this.save();
            this.$emit('closeDialog');
          },
          () => {
            Utils.showFailureSnackbar(this.$t('book/issue:invoiceDocumentation'));
          }
      )
    },
    processInvoice() {
      TaskBoardService.processInvoice(
          this.invoiceDocumentation.task,
          this.invoiceDocumentation.id,
          () => {
            Utils.showSuccessSnackbar(this.$t('book/issue:invoiceDocumentation'));
            this.recentlyProcessed = true;
            this.save();
            this.$emit('closeDialog');
          },
          () => {
            Utils.showFailureSnackbar(this.$t('book/issue:invoiceDocumentation'));
          }
      )
    },
    setFile(item) {
      if (item) {
        let docuToParse = {
          fileType: item.type,
          src: URL.createObjectURL(item)
        }
        this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
      } else {
        this.$refs.dragAndDrop.setPreviewSrc(null)
      }

      this.$refs.dragAndDrop.file = item;
      this.$refs.dragAndDrop.isUploading = false;
      this.file = item;
    },
    loadLiferantBySignedOffer(offerId) {
      OfferDocumentationService.readById(
          offerId,
          (response) => {
            if (response.data) {
              this.invoiceDocumentation.supplier = response.data.supplier;
            }
          },
          () => {
            Utils.showFailureSnackbar(this.$t('book/issue:offerDocumentation'));
          }
      );
    }
  }
}
</script>